import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Brain, Save, Lock } from 'lucide-react';

import { defaultExamData } from './neuro-exam/examDefaults';
import MentalStatusSection from './neuro-exam/MentalStatusSection';
import GaitPostureSection from './neuro-exam/GaitPostureSection';
import CranialNervesSection from './neuro-exam/CranialNervesSection';
import PosturalReactionsSection from './neuro-exam/PosturalReactionsSection';
import SpinalReflexesSection from './neuro-exam/SpinalReflexesSection';
import PainAssessmentSection from './neuro-exam/PainAssessmentSection';

const NeurologicalExamForm = ({ onSubmit, species, initialData, readOnly, isEdit }) => {
  const [activeSection, setActiveSection] = useState('mental');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialData ? { ...defaultExamData, ...initialData } : defaultExamData);

  // Sync form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({ ...defaultExamData, ...initialData });
    } else {
      setFormData(defaultExamData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id, readOnly, isEdit]);

  const handleChange = (field, value) => {
    if (readOnly) return;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
  };

  const getSubmitLabel = () => {
    if (loading) return isEdit ? 'Saving...' : 'Saving & Analyzing...';
    return isEdit ? 'Save Changes' : 'Save Exam & Generate Analysis';
  };

  const formTitle = readOnly ? 'Exam Details' : isEdit ? 'Edit Exam' : 'Neurological Examination';

  return (
    <Card className="border-[#E5E4E0] bg-white" data-testid="neuro-exam-form">
      <CardHeader>
        <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
          <Brain className="w-5 h-5 text-[#4B6651]" />
          {formTitle}
          {readOnly && (
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-[#5C5F5C]/10 text-[#5C5F5C] rounded text-xs font-medium">
              <Lock className="w-3 h-3" />
              Locked
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeSection} onValueChange={setActiveSection}>
          <TabsList className="bg-[#F8F7F4] border border-[#E5E4E0] p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="mental" className="text-xs data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="section-mental">
              Mental Status
            </TabsTrigger>
            <TabsTrigger value="gait" className="text-xs data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="section-gait">
              Gait/Posture
            </TabsTrigger>
            <TabsTrigger value="cranial" className="text-xs data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="section-cranial">
              Cranial Nerves
            </TabsTrigger>
            <TabsTrigger value="postural" className="text-xs data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="section-postural">
              Postural Reactions
            </TabsTrigger>
            <TabsTrigger value="reflexes" className="text-xs data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="section-reflexes">
              Spinal Reflexes
            </TabsTrigger>
            <TabsTrigger value="pain" className="text-xs data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="section-pain">
              Pain Assessment
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mental" className="mt-6">
            <MentalStatusSection formData={formData} handleChange={handleChange} />
          </TabsContent>

          <TabsContent value="gait" className="mt-6">
            <GaitPostureSection formData={formData} handleChange={handleChange} readOnly={readOnly} />
          </TabsContent>

          <TabsContent value="cranial" className="mt-6">
            <CranialNervesSection formData={formData} handleChange={handleChange} readOnly={readOnly} />
          </TabsContent>

          <TabsContent value="postural" className="mt-6">
            <PosturalReactionsSection formData={formData} handleChange={handleChange} readOnly={readOnly} />
          </TabsContent>

          <TabsContent value="reflexes" className="mt-6">
            <SpinalReflexesSection formData={formData} handleChange={handleChange} readOnly={readOnly} />
          </TabsContent>

          <TabsContent value="pain" className="mt-6">
            <PainAssessmentSection formData={formData} handleChange={handleChange} readOnly={readOnly} />
          </TabsContent>
        </Tabs>

        {!readOnly && (
          <div className="mt-8 pt-6 border-t border-[#E5E4E0]">
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full sm:w-auto bg-[#4B6651] hover:bg-[#3E5543] text-white"
              data-testid="submit-exam"
            >
              <Save className="w-4 h-4 mr-2" />
              {getSubmitLabel()}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NeurologicalExamForm;
